// 현장 목록 「오늘 할 일」 순서 — 기록 전 → 일지·사진 하나 빠짐 → 공정 지연 → 착공 7일 이내 → 나머지 → 끝난 현장, 같은 자리는 compareProjects(인메모리·Supabase 한 벌).

import { isProjectWorkday } from "./cron-select";
import { compareProjects } from "./list-order";
import { delayedPhaseCount, type SchedulePhase } from "./phase-schedule";
import type { Project } from "./types";
import { dDayOf } from "./view";

export const TODO_RANK = {
  NOT_RECORDED: 0,
  HALF_RECORDED: 1,
  DELAYED: 2,
  STARTS_SOON: 3,
  REST: 4,
  CLOSED: 5,
} as const;
export type TodoRank = (typeof TODO_RANK)[keyof typeof TODO_RANK];

export const STARTS_SOON_DAYS = 7;

// 시공중 현장의 오늘 형편 — 쉬는 날(workday false)이면 기록을 묻지 않는다(크론 log_reminder 와 같은 기준).
export interface ProjectTodaySignals {
  workday: boolean;
  logged: boolean;
  // null 은 사진 조회 실패(확인 불가) — 빠진 것으로 치지 않는다.
  photoToday: boolean | null;
  delayed: number;
}

// 오늘 형편을 볼 시공중 현장의 열 — 작업일 판정(work_*·schedule_holidays)과 「내 담당」(assignee_id).
export type ProjectWorkRef = Pick<
  Project,
  "id" | "assignee_id" | "work_saturday" | "work_sunday" | "work_holiday" | "schedule_holidays"
>;

// 시공중 현장마다 오늘 형편 한 벌 — 일지 id·오늘 사진 id·공정은 저장소가 묶음으로 읽어 온다(사진 id 가 null 이면 확인 불가).
export const todaySignalsOf = (
  refs: readonly ProjectWorkRef[],
  loggedIds: readonly string[],
  photoIds: readonly string[] | null,
  phases: readonly (SchedulePhase & { project_id: string })[],
  today: string
): Map<string, ProjectTodaySignals> => {
  const logged = new Set(loggedIds);
  const photo = photoIds === null ? null : new Set(photoIds);
  const phasesOf = new Map<string, SchedulePhase[]>();
  for (const phase of phases) phasesOf.set(phase.project_id, [...(phasesOf.get(phase.project_id) ?? []), phase]);
  return new Map(
    refs.map((ref) => [
      ref.id,
      {
        workday: isProjectWorkday(ref, today),
        logged: logged.has(ref.id),
        photoToday: photo === null ? null : photo.has(ref.id),
        delayed: delayedPhaseCount(phasesOf.get(ref.id) ?? [], today),
      },
    ])
  );
};

export type TodoProject = Pick<Project, "id" | "status" | "planned_start_date" | "created_at">;

// 기록을 물어야 하는 날인데 일지·사진 중 빠진 게 있다 — 둘 다 빠지면 NOT_RECORDED, 하나면 HALF_RECORDED(확인 불가 사진은 빠진 것이 아니다).
const recordRankOf = (signals: ProjectTodaySignals): TodoRank | null => {
  if (!signals.workday) return null;
  const photoMissing = signals.photoToday === false;
  if (!signals.logged && photoMissing) return TODO_RANK.NOT_RECORDED;
  if (!signals.logged || photoMissing) return TODO_RANK.HALF_RECORDED;
  return null;
};

export type RecordGap = "오늘 기록 전" | "일지 없음" | "사진 없음";

// 목록 알약 글자 — 빠진 게 없거나 묻지 않는 날이면 null.
export const recordGapOf = (signals: ProjectTodaySignals | undefined): RecordGap | null => {
  if (!signals) return null;
  const rank = recordRankOf(signals);
  if (rank === TODO_RANK.NOT_RECORDED) return "오늘 기록 전";
  if (rank === TODO_RANK.HALF_RECORDED) return signals.logged ? "사진 없음" : "일지 없음";
  return null;
};

export const todoRankOf = (
  project: Pick<Project, "status" | "planned_start_date">,
  signals: ProjectTodaySignals | undefined,
  today: string
): TodoRank => {
  if (project.status === "CLOSED") return TODO_RANK.CLOSED;
  if (project.status === "IN_PROGRESS" && signals) {
    const record = recordRankOf(signals);
    if (record !== null) return record;
    if (signals.delayed > 0) return TODO_RANK.DELAYED;
  }
  if (project.status === "SCHEDULED") {
    const days = dDayOf(project.planned_start_date, today);
    if (days !== null && days <= STARTS_SOON_DAYS) return TODO_RANK.STARTS_SOON;
  }
  return TODO_RANK.REST;
};

// 오늘 기록을 아직 다 안 한 현장인가 — 목록 머리의 「오늘 기록 안 한 현장 N곳」.
export const needsRecordToday = (signals: ProjectTodaySignals | undefined): boolean =>
  signals !== undefined && recordRankOf(signals) !== null;

export const compareTodo =
  (signalsOf: ReadonlyMap<string, ProjectTodaySignals>, today: string) =>
  (a: TodoProject, b: TodoProject): number =>
    todoRankOf(a, signalsOf.get(a.id), today) - todoRankOf(b, signalsOf.get(b.id), today) || compareProjects(a, b);
